/**
 * Faz 9: Dosya Transferi — gerçek signaling browse RPC + FileSender wire-up.
 *
 * Aktif bir session yoksa "Önce bir cihaza bağlan" mesajı; aktif session
 * varsa sol pane'de yerel dosyalar (input file ile sürükleme), sağ pane'de
 * uzak filesystem (signaling browse RPC). Sağ → Sol/Sol → Sağ butonları:
 *   Sol → Sağ: yerel dosyaları FileSender ile uzak ana bilgisayara gönder.
 *   Sağ → Sol: (Faz 9.5 — host → controller pull henüz yok; UI passive.)
 */
import { applyStyle, escapeHtml } from '../ui/css';
import { createButton } from '../ui/components/button';
import { createPageHeader } from '../ui/components/pageHeader';
import { icons } from '../ui/icons';
import { activeSession } from '../services/active-session';
export function renderFileTransfer() {
    const root = document.createElement('div');
    const sess = activeSession.get();
    if (!sess) {
        root.append(createPageHeader({
            title: 'Dosya',
            highlight: 'Transferi',
            subtitle: 'Aktif bir oturum yok',
        }));
        const empty = document.createElement('div');
        applyStyle(empty, {
            padding: 'var(--sp-6)',
            textAlign: 'center',
            background: 'var(--bg-elev-2)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-secondary)',
        });
        empty.innerHTML = `
      <div style="font-size:var(--fs-h2);color:var(--text-primary);margin-bottom:var(--sp-2)">Önce bir cihaza bağlanın</div>
      <div style="color:var(--text-tertiary)">Dosya transferi yalnız aktif bir uzaktan kontrol oturumunda kullanılabilir.</div>
    `;
        root.append(empty);
        return root;
    }
    const { signaling, fileSender } = sess;
    const browse = sess.browse;
    // ── State ──
    let remoteEntries = [];
    let remotePath = null;
    let remoteParent = null;
    let remoteSelected = new Set();
    let remoteError = null;
    let remoteLoading = true;
    const localFiles = [];
    let localSelected = new Set();
    const queue = [];
    // ── Header ──
    const speedBadge = document.createElement('div');
    applyStyle(speedBadge, {
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px',
    });
    function updateSpeed() {
        const totalBytes = queue.reduce((s, q) => s + q.bytesDone, 0);
        const active = queue.filter(q => q.status === 'running').length;
        speedBadge.innerHTML = `
      <span style="font-size:var(--fs-tiny);letter-spacing:0.06em;color:var(--text-tertiary)">AKTARIM</span>
      <span style="font-family:var(--font-mono);font-size:var(--fs-h3);color:${active > 0 ? 'var(--accent)' : 'var(--text-tertiary)'};letter-spacing:0.04em">${formatBytes(totalBytes)}</span>
    `;
    }
    updateSpeed();
    root.append(createPageHeader({
        title: 'Dosya',
        highlight: 'Transferi',
        subtitle: `Hedef ${sess.peerDeviceId.substring(0, 8)} · AES-256 şifreli kanal`,
        rightSlot: speedBadge,
    }));
    // ── Stage ──
    const stage = document.createElement('div');
    stage.classList.add('pr-stage-grid', 'pr-stage-grid--3col');
    applyStyle(stage, {
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        gap: 'var(--sp-3)',
        minHeight: '380px',
    });
    // Local pane
    const leftWrap = document.createElement('div');
    function renderLocal() {
        leftWrap.replaceChildren(buildLocalPane());
    }
    function buildLocalPane() {
        const card = paneShell('YEREL', 'Tarayıcı dosyaları');
        const dropZone = document.createElement('label');
        applyStyle(dropZone, {
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed var(--border-strong)',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--sp-4)',
            gap: 'var(--sp-1)',
            cursor: 'pointer',
            color: 'var(--text-tertiary)',
            fontSize: 'var(--fs-caption)',
        });
        dropZone.innerHTML = `
      <span style="color:var(--accent);font-size:var(--fs-h3);font-family:var(--font-mono)">+</span>
      <span>Dosya seç veya bırak</span>
    `;
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.style.display = 'none';
        input.addEventListener('change', () => {
            if (input.files) {
                for (const f of Array.from(input.files))
                    localFiles.push(f);
                renderLocal();
            }
        });
        dropZone.append(input);
        dropZone.addEventListener('dragover', (e) => { e.preventDefault(); });
        dropZone.addEventListener('drop', (e) => {
            e.preventDefault();
            if (e.dataTransfer?.files) {
                for (const f of Array.from(e.dataTransfer.files))
                    localFiles.push(f);
                renderLocal();
            }
        });
        card.append(dropZone);
        const list = document.createElement('div');
        applyStyle(list, { display: 'flex', flexDirection: 'column', gap: '1px', flex: '1' });
        if (localFiles.length === 0) {
            const hint = document.createElement('div');
            hint.textContent = 'Henüz seçili dosya yok.';
            applyStyle(hint, { padding: 'var(--sp-3)', color: 'var(--text-tertiary)', fontSize: 'var(--fs-caption)' });
            list.append(hint);
        }
        localFiles.forEach((f, idx) => {
            const sel = localSelected.has(f.name + ':' + idx);
            const row = fileRow({
                name: f.name,
                size: f.size,
                modifiedLabel: '',
                isDir: false,
                selected: sel,
                onClick: () => {
                    const k = f.name + ':' + idx;
                    if (localSelected.has(k))
                        localSelected.delete(k);
                    else
                        localSelected.add(k);
                    renderLocal();
                    updateDirBtns();
                },
            });
            list.append(row);
        });
        card.append(list);
        return card;
    }
    // Remote pane
    const rightWrap = document.createElement('div');
    function renderRemote() {
        rightWrap.replaceChildren(buildRemotePane());
    }
    function buildRemotePane() {
        const card = paneShell('UZAK', 'Bağlı bilgisayar');
        // Path bar
        const bar = document.createElement('div');
        applyStyle(bar, {
            display: 'flex', alignItems: 'center', gap: 'var(--sp-1)',
            fontSize: 'var(--fs-caption)', color: 'var(--text-tertiary)',
            fontFamily: 'var(--font-mono)',
        });
        if (remoteParent !== null || remotePath !== null) {
            const upBtn = createButton({
                label: '↑',
                variant: 'ghost', size: 'sm',
                onClick: () => {
                    loadRemote(remoteParent);
                },
            });
            bar.append(upBtn);
        }
        const pathSpan = document.createElement('span');
        pathSpan.textContent = remotePath ?? '(kök)';
        pathSpan.style.color = 'var(--text-primary)';
        pathSpan.style.flex = '1';
        pathSpan.style.overflow = 'hidden';
        pathSpan.style.textOverflow = 'ellipsis';
        pathSpan.style.whiteSpace = 'nowrap';
        bar.append(pathSpan);
        const refreshBtn = createButton({
            label: '↻', variant: 'ghost', size: 'sm',
            onClick: () => loadRemote(remotePath),
        });
        bar.append(refreshBtn);
        card.append(bar);
        const list = document.createElement('div');
        applyStyle(list, { display: 'flex', flexDirection: 'column', gap: '1px', flex: '1' });
        if (remoteLoading) {
            const hint = document.createElement('div');
            hint.textContent = 'Yükleniyor…';
            applyStyle(hint, { padding: 'var(--sp-3)', color: 'var(--text-tertiary)', fontSize: 'var(--fs-caption)' });
            list.append(hint);
        }
        else if (remoteError) {
            const hint = document.createElement('div');
            hint.textContent = `Hata: ${remoteError}`;
            applyStyle(hint, { padding: 'var(--sp-3)', color: 'var(--status-error)', fontSize: 'var(--fs-caption)' });
            list.append(hint);
        }
        else if (remoteEntries.length === 0) {
            const hint = document.createElement('div');
            hint.textContent = 'Boş klasör.';
            applyStyle(hint, { padding: 'var(--sp-3)', color: 'var(--text-tertiary)', fontSize: 'var(--fs-caption)' });
            list.append(hint);
        }
        else {
            for (const e of remoteEntries) {
                const sel = remoteSelected.has(e.fullPath);
                const row = fileRow({
                    name: e.name,
                    size: e.size,
                    modifiedLabel: shortDate(e.modifiedAt),
                    isDir: e.isDirectory,
                    selected: sel,
                    onClick: () => {
                        if (e.isDirectory) {
                            loadRemote(e.fullPath);
                            return;
                        }
                        if (remoteSelected.has(e.fullPath))
                            remoteSelected.delete(e.fullPath);
                        else
                            remoteSelected.add(e.fullPath);
                        renderRemote();
                        updateDirBtns();
                    },
                });
                list.append(row);
            }
        }
        card.append(list);
        return card;
    }
    function paneShell(caption, title) {
        const card = document.createElement('section');
        applyStyle(card, {
            background: 'var(--bg-elev-2)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
            minWidth: '0',
        });
        const header = document.createElement('div');
        applyStyle(header, {
            paddingBottom: 'var(--sp-3)',
            borderBottom: '1px solid var(--border-subtle)',
        });
        header.innerHTML = `
      <div style="font-size:var(--fs-tiny);letter-spacing:0.16em;color:var(--text-tertiary);font-weight:500;margin-bottom:2px">${escapeHtml(caption)}</div>
      <div style="font-size:var(--fs-h3);font-weight:600;color:var(--text-primary)">${escapeHtml(title)}</div>
    `;
        card.append(header);
        return card;
    }
    function fileRow(opts) {
        const row = document.createElement('div');
        applyStyle(row, {
            display: 'grid',
            gridTemplateColumns: '20px 1fr 80px 70px',
            gap: 'var(--sp-2)',
            alignItems: 'center',
            padding: '8px 12px',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--fs-caption)',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            background: opts.selected ? 'var(--accent-soft)' : 'transparent',
            border: opts.selected ? '1px solid var(--accent)' : '1px solid transparent',
            userSelect: 'none',
        });
        row.addEventListener('click', opts.onClick);
        row.addEventListener('mouseenter', () => {
            if (!opts.selected)
                row.style.background = 'var(--bg-elev-3)';
        });
        row.addEventListener('mouseleave', () => {
            if (!opts.selected)
                row.style.background = 'transparent';
        });
        const ic = document.createElement('span');
        ic.innerHTML = opts.isDir
            ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>'
            : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>';
        ic.style.color = opts.isDir ? 'var(--accent)' : (opts.selected ? 'var(--accent)' : 'var(--text-tertiary)');
        row.append(ic);
        const name = document.createElement('span');
        name.textContent = opts.name;
        applyStyle(name, {
            color: opts.selected ? 'var(--text-primary)' : 'var(--text-secondary)',
            fontWeight: opts.isDir ? '500' : '400',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        });
        row.append(name);
        const size = document.createElement('span');
        size.textContent = opts.isDir ? '— KLASÖR' : formatBytes(opts.size);
        applyStyle(size, {
            fontSize: 'var(--fs-tiny)', color: 'var(--text-tertiary)',
            textAlign: 'right', fontFamily: 'var(--font-mono)',
        });
        row.append(size);
        const date = document.createElement('span');
        date.textContent = opts.modifiedLabel;
        applyStyle(date, {
            fontSize: 'var(--fs-tiny)', color: 'var(--text-tertiary)', textAlign: 'right',
        });
        row.append(date);
        return row;
    }
    // ── Direction buttons ──
    const dirCol = document.createElement('div');
    dirCol.classList.add('pr-stage-mid');
    applyStyle(dirCol, {
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        gap: 'var(--sp-2)', paddingTop: '60px',
    });
    const sendRightBtn = createButton({
        label: '',
        variant: 'primary',
        iconHtml: icons.arrowRight(),
        attrs: { title: 'Yerel → Uzak (gönder)' },
    });
    applyStyle(sendRightBtn, { width: '40px', height: '40px', padding: '0' });
    dirCol.append(sendRightBtn);
    function updateDirBtns() {
        sendRightBtn.disabled = localSelected.size === 0 || !fileSender;
    }
    updateDirBtns();
    sendRightBtn.addEventListener('click', () => {
        if (!fileSender)
            return;
        for (const key of localSelected) {
            const colon = key.lastIndexOf(':');
            if (colon < 0)
                continue;
            const idx = parseInt(key.substring(colon + 1), 10);
            const f = localFiles[idx];
            if (!f)
                continue;
            const item = {
                id: crypto.randomUUID(),
                name: f.name,
                size: f.size,
                bytesDone: 0,
                status: 'queued',
                file: f,
            };
            queue.push(item);
            void runItem(item);
        }
        localSelected.clear();
        renderLocal();
        renderQueue();
        updateDirBtns();
    });
    stage.append(leftWrap, dirCol, rightWrap);
    root.append(stage);
    // ── Queue UI ──
    const queueCard = document.createElement('section');
    applyStyle(queueCard, {
        background: 'var(--bg-elev-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--sp-4)',
        marginTop: 'var(--sp-4)',
    });
    const queueTitle = document.createElement('h3');
    applyStyle(queueTitle, {
        margin: '0 0 var(--sp-3) 0', fontSize: 'var(--fs-h3)', color: 'var(--text-primary)',
    });
    queueTitle.textContent = 'Aktif transferler · 0';
    queueCard.append(queueTitle);
    const queueList = document.createElement('div');
    applyStyle(queueList, { display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' });
    queueCard.append(queueList);
    root.append(queueCard);
    function renderQueue() {
        queueTitle.textContent = `Aktif transferler · ${queue.length}`;
        queueList.innerHTML = '';
        for (const item of queue) {
            const row = document.createElement('div');
            row.classList.add('pr-row-flex');
            applyStyle(row, {
                display: 'grid',
                gridTemplateColumns: '24px 1fr 1fr 70px 80px',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                padding: '8px 12px',
                background: 'var(--bg-elev-3)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--fs-caption)',
            });
            const ic = document.createElement('span');
            if (item.status === 'done')
                ic.innerHTML = '<span style="color:var(--status-online)">✓</span>';
            else if (item.status === 'error')
                ic.innerHTML = '<span style="color:var(--status-error)">×</span>';
            else
                ic.innerHTML = '<span style="color:var(--accent)">→</span>';
            row.append(ic);
            const name = document.createElement('span');
            name.textContent = item.name;
            applyStyle(name, { color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });
            row.append(name);
            const bar = document.createElement('div');
            applyStyle(bar, { height: '6px', background: 'var(--bg-elev-2)', borderRadius: '3px', overflow: 'hidden' });
            const fill = document.createElement('div');
            const pct = item.size > 0 ? (item.bytesDone / item.size) * 100 : 0;
            applyStyle(fill, {
                height: '100%',
                width: `${pct}%`,
                background: item.status === 'done'
                    ? 'var(--status-online)'
                    : 'linear-gradient(90deg, var(--accent), #d4ff3a)',
            });
            bar.append(fill);
            row.append(bar);
            const pctText = document.createElement('span');
            pctText.textContent = `${Math.round(pct)}%`;
            applyStyle(pctText, { fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-tiny)', color: 'var(--text-secondary)', textAlign: 'right' });
            row.append(pctText);
            const status = document.createElement('span');
            status.textContent = item.status === 'error'
                ? (item.error ?? 'hata')
                : item.status === 'done' ? 'tamamlandı'
                    : item.status === 'running' ? 'gönderiliyor'
                        : 'kuyrukta';
            applyStyle(status, { fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-tiny)', color: 'var(--text-tertiary)', textAlign: 'right' });
            row.append(status);
            queueList.append(row);
        }
    }
    async function runItem(item) {
        if (!fileSender || !item.file) {
            item.status = 'error';
            item.error = 'transfer kanalı yok';
            renderQueue();
            return;
        }
        item.status = 'running';
        renderQueue();
        try {
            const res = await fileSender.sendFile(item.file, (p) => {
                item.bytesDone = p.bytesSent;
                renderQueue();
                updateSpeed();
            });
            if (res.ok) {
                item.status = 'done';
                item.bytesDone = item.size;
            }
            else {
                item.status = 'error';
                item.error = res.error ?? 'fail';
            }
        }
        catch (e) {
            item.status = 'error';
            item.error = String(e);
        }
        renderQueue();
        updateSpeed();
    }
    // ── Initial load ──
    renderLocal();
    renderRemote();
    async function loadRemote(path) {
        remoteLoading = true;
        remoteError = null;
        remoteSelected.clear();
        renderRemote();
        try {
            const resp = await browse(path);
            remoteEntries = resp.entries ?? [];
            remotePath = resp.path ?? null;
            remoteParent = resp.parentPath ?? null;
            remoteError = resp.error ?? null;
        }
        catch (e) {
            remoteError = String(e);
            remoteEntries = [];
        }
        finally {
            remoteLoading = false;
            renderRemote();
            updateDirBtns();
        }
    }
    void loadRemote(null);
    // Cleanup: signaling.send referansı tutuyor — sayfa kaldırılınca özel
    // bir teardown gerekmez; aktiv session referansları session page yönetiyor.
    void signaling; // keep ref so closure compiler tutar
    return root;
}
function formatBytes(b) {
    if (b < 1024)
        return `${Math.floor(b)} B`;
    if (b < 1024 * 1024)
        return `${(b / 1024).toFixed(0)} KB`;
    if (b < 1024 * 1024 * 1024)
        return `${(b / 1024 / 1024).toFixed(1)} MB`;
    return `${(b / 1024 / 1024 / 1024).toFixed(1)} GB`;
}
function shortDate(iso) {
    const d = new Date(iso);
    if (isNaN(d.getTime()))
        return '';
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }).toUpperCase();
}
